import { ArrowLeftOutlined, EyeOutlined, PictureOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Empty, Image, Input, Modal, Row, Space, Spin, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type HangHoaModel from '../../models/hang_hoa';
import hangHoaService from '../../services/hangHoaService';
import { hasRecipeContent, renderRecipeContent } from '../../utils/renderRecipeContent';

const { Text, Title } = Typography;

const formatMoney = (value: number) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);

const normalizeSearchText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd');

export default function DanhSachMonAnPage() {
  const navigate = useNavigate();
  const [dishes, setDishes] = useState<HangHoaModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<HangHoaModel | null>(null);

  const loadDishes = async () => {
    setIsLoading(true);
    try {
      setDishes(await hangHoaService.getDanhSachMonAnChoBep());
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Không thể tải danh sách món ăn');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadDishes();
  }, []);

  const filteredDishes = useMemo(() => {
    const searchTerm = normalizeSearchText(searchText.trim());
    return dishes.filter((dish) =>
      normalizeSearchText(`${dish.ten} ${dish.mo_ta_hang_hoa || ''}`).includes(searchTerm)
    );
  }, [dishes, searchText]);
  const recipeContent = selectedRecipe?.nguyen_lieu_cach_che_bien
    ? renderRecipeContent(selectedRecipe.nguyen_lieu_cach_che_bien)
    : [];
  const selectedRecipeHasContent = hasRecipeContent(selectedRecipe?.nguyen_lieu_cach_che_bien);

  return (
    <main className="kitchen-page kitchen-dishes-page">
      <div className="kitchen-header">
        <div className="kitchen-heading">
          <Title level={2} style={{ margin: 0 }}>Danh sách món ăn</Title>
          <Text type="secondary">Tất cả món ăn hiện có trong thực đơn ({dishes.length})</Text>
        </div>
        <Space wrap className="kitchen-dish-header-actions">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/quan-ly-bep')}>
            Quay lại quản lý bếp
          </Button>
          <Button icon={<ReloadOutlined />} loading={isLoading} onClick={() => void loadDishes()}>
            Làm mới
          </Button>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
          action={<Button size="small" onClick={() => void loadDishes()}>Thử lại</Button>}
        />
      )}

      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            className="kitchen-dish-search"
            placeholder="Tìm theo tên hoặc mô tả món ăn"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            style={{ maxWidth: 420 }}
          />
          <Spin spinning={isLoading}>
            {filteredDishes.length > 0 ? (
              <Row gutter={[16, 16]} className="kitchen-dish-grid">
                {filteredDishes.map((dish) => {
                  const imageUrl = dish.hinh_anh_hang_hoas?.[0]?.full_path
                    || dish.hinh_anh_hang_hoas?.[0]?.path;

                  return (
                    <Col xs={24} sm={12} lg={8} xl={6} key={dish.id ?? dish.key_value}>
                      <Card
                        hoverable
                        className="kitchen-dish-card"
                        cover={imageUrl ? (
                          <Image
                            src={imageUrl}
                            alt={dish.ten}
                            preview={false}
                            style={{ height: 190, objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            className="kitchen-dish-image-placeholder"
                            style={{
                              height: 190,
                              display: 'grid',
                              placeItems: 'center',
                              background: '#f5f5f5',
                              color: '#8c8c8c',
                            }}
                          >
                            <PictureOutlined style={{ fontSize: 36 }} />
                          </div>
                        )}
                      >
                        <Space direction="vertical" size={6} style={{ width: '100%' }}>
                          <Space className="kitchen-dish-name-row" style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Text strong style={{ fontSize: 16 }}>{dish.ten}</Text>
                            <Button
                              type="link"
                              className="kitchen-dish-recipe-button"
                              icon={<EyeOutlined style={{ fontSize: '200%' }} />}
                              aria-label={`Xem nguyên liệu và cách chế biến: ${dish.ten}`}
                              title="Xem nguyên liệu và cách chế biến"
                              onClick={() => setSelectedRecipe(dish)}
                            >
                              Cách chế biến
                            </Button>
                          </Space>
                          <Text type="danger" strong>{formatMoney(dish.gia_ban_hien_thi ?? dish.gia_ban)}</Text>
                          {dish.mo_ta_hang_hoa && (
                            <Text type="secondary">{dish.mo_ta_hang_hoa}</Text>
                          )}
                        </Space>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              !isLoading && !error && (
                <Empty description={searchText ? 'Không tìm thấy món ăn' : 'Chưa có món ăn nào'} />
              )
            )}
          </Spin>
        </Space>
      </Card>
      <Modal
        className="kitchen-modal kitchen-dishes-recipe-modal"
        title={`Nguyên liệu và cách chế biến${selectedRecipe ? ` - ${selectedRecipe.ten}` : ''}`}
        open={selectedRecipe !== null}
        onCancel={() => setSelectedRecipe(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedRecipe(null)}>
            Đóng
          </Button>,
        ]}
      >
        {selectedRecipeHasContent ? (
          <div className="recipe-content">{recipeContent}</div>
        ) : (
          <Empty description="Chưa có thông tin nguyên liệu và cách chế biến" />
        )}
      </Modal>
    </main>
  );
}
